<template>
  <div class="selectbox" :class="{ disabled: readonly, error }" @click="onClickSelectBox">
    <div class="selected">
      <div v-if="text" class="text">{{ text }}</div>
      <SelectBoxItem
        v-else-if="selected"
        :id="selected.id"
        :name="selected.name"
        :image="selected.image || placeholderImage"
        :sub-name="nolaNovelTitle"
      />
      <div v-else class="placeholder">{{ allowUnselected ? $t("common.unselected") : placeholder }}</div>
      <ChevronDownIcon v-if="!readonly" class="caret" :class="{ open: isOpen }" :fillColor="accentColor" />
    </div>

    <div v-if="isOpen" class="dropdown">
      <SelectBoxItem
        v-if="allowUnselected"
        class="item"
        :name="$t('common.unselected')"
        :image="placeholderImage"
        :key="undefined"
        @click="onClickItem(undefined)"
      />
      <div class="section-title">
        {{ $t("contestNolaNovelSelectItem.eligibleWorksTitle") }}
        <aside>{{ $t("contestNolaNovelSelectItem.eligibleWorksNote") }}</aside>
      </div>
      <template v-for="option in categorized.nolaNovelAssociated">
        <div
          :key="option.id"
          class="select-box-container"
          :class="{ ['selected-item']: option.id == selected?.id }"
          @click="onClickItem(option)"
        >
          <ImageView class="image" :src="option.image || placeholderImage" />
          <div class="name">
            <div class="novel-title">{{ option.name }}</div>
            <div class="nola-novel-title" :class="{ ['selected-item']: option.id == selected?.id }">
              {{ $t("contestNolaNovelSelectItem.nolaNovelWorkLabel")
              }}{{ nolaNovelRelatedNovels[option.associatedData?.nolaNovel?.id]?.title || "-" }}
            </div>
          </div>
          <div
            class="label"
            :class="{
              ['limited-public']: option.isLimitedPublic,
              ['public']: option.isPublic,
              ['private']: option.associatedData?.nolaNovel?.id && !option.isLimitedPublic && !option.isPublic,
            }"
          >
            <template v-if="option.isPublic">{{ $t("contestNolaNovelSelectItem.statusLabels.public") }}</template>
            <template v-else-if="option.isLimitedPublic">{{
              $t("contestNolaNovelSelectItem.statusLabels.limitedPublic")
            }}</template>
            <template v-else-if="option.associatedData?.nolaNovel?.id">{{
              $t("contestNolaNovelSelectItem.statusLabels.private")
            }}</template>
            <template v-else>{{ $t("contestNolaNovelSelectItem.statusLabels.unposted") }}</template>
          </div>
        </div>
      </template>
      <div class="section-title">
        {{ $t("contestNolaNovelSelectItem.ineligibleWorksTitle") }}
        <aside>{{ $t("contestNolaNovelSelectItem.ineligibleWorksNote") }}</aside>
      </div>
      <template v-for="option in categorized.other">
        <div :key="option.id" class="select-box-container disabled" @click="onClickDisabledItem(option)">
          <ImageView class="image" :src="option.image || placeholderImage" />
          <div class="name">
            <div class="novel-title">{{ option.name }}</div>
            <div class="nola-novel-title">
              {{ $t("contestNolaNovelSelectItem.nolaNovelWorkLabel")
              }}{{ nolaNovelRelatedNovels[option.associatedData?.nolaNovel.id]?.title || "-" }}
            </div>
          </div>
          <div
            class="label"
            :class="{
              ['limited-public']: option.isLimitedPublic,
              ['public']: option.isPublic,
              ['private']: option.associatedData?.nolaNovel?.id && !option.isLimitedPublic && !option.isPublic,
            }"
          >
            <template v-if="option.isPublic">{{ $t("contestNolaNovelSelectItem.statusLabels.public") }}</template>
            <template v-else-if="option.isLimitedPublic">{{
              $t("contestNolaNovelSelectItem.statusLabels.limitedPublic")
            }}</template>
            <template v-else-if="option.associatedData?.nolaNovel?.id">{{
              $t("contestNolaNovelSelectItem.statusLabels.private")
            }}</template>
            <template v-else>{{ $t("contestNolaNovelSelectItem.statusLabels.unposted") }}</template>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import SelectBoxItem from "@/components/atoms/SelectBoxItem.vue";
import ChevronDownIcon from "icons/ChevronDown.vue";
import ImageView from "@/components/atoms/ImageView.vue";
import { Dialog } from "@/lib/utils/dialog";
import NolaNovelContestConfirmDialog from "@/components/ui/dialogs/NolaNovelContestConfirmDialog.vue";
import { Auth } from "aws-amplify";
import { NovelFromNolaNovel } from "@/lib/models/nolaNovel";

const ClickOutside = require("vue-click-outside");

export default Vue.extend<Data, Methods, Computed, Props>({
  directives: { ClickOutside },
  components: { SelectBoxItem, ChevronDownIcon, ImageView },
  model: {
    prop: "selected",
    event: "select",
  },
  props: {
    selected: Object as PropType<Option>,
    initialSelected: Object as PropType<object>,
    options: Array as PropType<Option[]>,
    placeholder: String as PropType<string>,
    placeholderImage: String as PropType<string>,
    onClickHandler: Function as PropType<() => void>,
    publishCondition: Array as PropType<string[] | null>,
    text: String as PropType<string>,
    allowUnselected: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    readonly: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    error: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
      themeWatcher: null as MutationObserver | null,
    };
  },
  async created() {
    if (this.initialSelected) {
      this.$emit("select", this.initialSelected);
    }

    await this.$store.dispatch("novelModule/initialize");
    await this.$store.dispatch("nolaNovelModule/fetchNovels");

    // テーマ変更を検知するMutationObserverを設定
    if (typeof window !== "undefined") {
      this.themeWatcher = new MutationObserver(() => {
        // テーマ変更時にcomputed propertyを再計算させるため、$forceUpdateを呼び出す
        this.$forceUpdate();
      });
      this.themeWatcher.observe(document.documentElement, {
        attributes: true,
        // テーマは data-app-theme 属性で切り替わるため、ここを監視する
        attributeFilter: ["data-app-theme"],
      });
    }
  },
  beforeDestroy() {
    if (this.themeWatcher) {
      this.themeWatcher.disconnect();
    }
  },
  methods: {
    async onClickSelectBox() {
      if (this.readonly) return;

      if (this.onClickHandler) {
        this.onClickHandler();
      } else {
        this.isOpen = !this.isOpen;
      }

      if (this.isOpen) {
        await this.$store.dispatch("novelModule/initialize");
        await this.$store.dispatch("nolaNovelModule/fetchNovels");
      }
    },
    onClickItem(item) {
      this.$emit("select", item);
    },
    onClickOutside() {
      this.isOpen = false;
    },
    async onClickDisabledItem(item) {
      if (!item) return;

      const dialog = new Dialog(NolaNovelContestConfirmDialog);
      const data = {
        ...item,
        nolaNovelId: item.associatedData?.nolaNovel.id as string | undefined,
        publishCondition: this.publishCondition,
      };

      const result = await dialog.show(data);
      if (!result) {
        return;
      }

      if (result === "reopen") {
        this.isOpen = true;
        return;
      }

      const nolaNovelId = this.nolaNovelRelatedNovels[item.associatedData?.nolaNovel.id]?.id;
      const params = new URLSearchParams();

      let url: string;

      if (!nolaNovelId && result === "/novel/create") {
        url = `${process.env.VUE_APP_NOLANOVEL_WEB}${result}`;
        params.append("novelid", item.id as string);
      } else if (nolaNovelId && result === "/episode/create") {
        url = `${process.env.VUE_APP_NOLANOVEL_WEB}${result}/${nolaNovelId}`;
      } else {
        url = `${process.env.VUE_APP_NOLANOVEL_WEB}/mypage/novel/${nolaNovelId}`;
      }

      const user = await Auth.currentUserPoolUser();
      const { idToken } = user.signInUserSession;
      params.append("token", idToken.jwtToken);
      params.append("userId", user.attributes.sub);
      window.open(`${url}?${params.toString()}`, "_blank");
    },
  },
  computed: {
    accentColor() {
      // CSS変数からアクセント色を取得
      if (typeof window !== "undefined") {
        const computedStyle = getComputedStyle(document.documentElement);
        return computedStyle.getPropertyValue("--color-primary").trim() || "#76a4d3";
      }
      return "#76a4d3";
    },
    nolaNovelRelatedNovels() {
      const novels: NovelFromNolaNovel[] = this.$store.getters["nolaNovelModule/novels"];
      const items = {} as { [novelId: string]: any };
      novels.forEach((novel) => {
        if (this.options.find((option) => option.associatedData?.nolaNovel.id === novel.id)) {
          items[novel.id] = novel;
        }
      });
      return items;
    },
    categorized() {
      const novels = this.nolaNovelRelatedNovels;

      const res = {
        nolaNovelAssociated: [] as NovelCategorized[],
        other: [] as NovelCategorized[],
      };

      this.options.forEach((option: Option) => {
        if (option.associatedData?.nolaNovel) {
          const nolaNovelData = novels[option.associatedData.nolaNovel.id];
          const optionData = {
            ...option,
            isPublic: nolaNovelData?.isPublic,
            isLimitedPublic: nolaNovelData?.isLimitedPublic,
            isPrivate: !nolaNovelData?.isPublic && !nolaNovelData?.isLimitedPublic,
            hasEpisodes: nolaNovelData?.episodes.length > 0,
            published: Boolean(nolaNovelData),
            updatedAt: nolaNovelData?.updatedAt,
          };

          if (!nolaNovelData) {
            res.other.push(optionData);
          } else if (!this.publishCondition || this.publishCondition.length === 0) {
            res.other.push(optionData);
          } else if (!optionData.hasEpisodes) {
            res.other.push(optionData);
          } else if (this.publishCondition.includes("limitedPublic") && nolaNovelData.isLimitedPublic) {
            res.nolaNovelAssociated.push({ ...optionData });
          } else if (this.publishCondition.includes("public") && nolaNovelData.isPublic) {
            res.nolaNovelAssociated.push({ ...optionData });
          } else {
            res.other.push(optionData);
          }
        } else {
          res.other.push(option);
        }
      });

      /** Booleanの並び順を -1, 0, 1 で返す */
      function sortBoolean(a: boolean | undefined, b: boolean | undefined) {
        if (a && !b) return -1;
        if (!a && b) return 1;
        return 0;
      }

      function sortFunc(a: NovelCategorized, b: NovelCategorized) {
        // 並び順を、公開中＞限定公開＞非公開＞未投稿かつ更新日の降順にする
        return (
          sortBoolean(a.isPublic, b.isPublic) ||
          sortBoolean(a.isLimitedPublic, b.isLimitedPublic) ||
          sortBoolean(a.isPrivate, b.isPrivate) ||
          sortBoolean(a.published, b.published) ||
          (b.updatedAt || 0) - (a.updatedAt || 0) ||
          (b.updatedAtLatestDataInNovel || 0) - (a.updatedAtLatestDataInNovel || 0)
        );
      }
      res.other.sort(sortFunc);
      res.nolaNovelAssociated.sort(sortFunc);
      return res;
    },
    nolaNovelTitle() {
      if (!this.selected) return null;

      const nolaNovelId = this.selected.associatedData?.nolaNovel?.id;
      if (!nolaNovelId) return null;

      const novelFromNolaNovel = this.nolaNovelRelatedNovels[nolaNovelId];
      if (!novelFromNolaNovel) return null;

      return `${this.$t("contestNolaNovelSelectItem.nolaNovelWorkLabel")}${novelFromNolaNovel.title}`;
    },
  },
});

interface Props {
  selected: Option;
  initialSelected?: object;
  options: Option[];
  placeholder: string;
  placeholderImage?: string;
  onClickHandler?: () => void;
  text?: string;
  publishCondition?: string[] | null;
  allowUnselected: boolean;
  readonly: boolean;
  error: boolean;
}

interface Data {
  isOpen: boolean;
  themeWatcher: MutationObserver | null;
}

interface Computed {
  accentColor: string;
  categorized: {
    /** 連携中 */
    nolaNovelAssociated: NovelCategorized[];
    /** その他 */
    other: NovelCategorized[];
  };
  nolaNovelRelatedNovels: { [novelId: string]: any };
  nolaNovelTitle: string | null;
}

interface NovelCategorized extends Option {
  isPublic?: boolean;
  isLimitedPublic?: boolean;
  isPrivate?: boolean;
  hasEpisodes?: boolean;
  published?: boolean;
  updatedAt?: number;
}

interface Methods {
  onClickSelectBox(): void;
  onClickItem(item?: Option): void;
  onClickDisabledItem(item?: NovelCategorized): void;
  onClickOutside(): void;
}

export type Option = {
  id: string | number;
  name: string;
  image?: string;
  associatedData?: any;
  updatedAtLatestDataInNovel?: number;
};
</script>

<style lang="scss" scoped>
.selectbox {
  position: relative;
  box-sizing: border-box;
  border: 1px solid var(--color-primary);
  border-radius: var(--radius-xs);
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);

  &.disabled {
    border-color: var(--color-border);
    pointer-events: none;
  }

  &.error {
    border-color: var(--color-warning);
  }

  .selected {
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-sizing: border-box;
    padding: 10px 20px;
    background-color: var(--color-background-normal);
    color: var(--color-text-normal);

    .text {
      font-weight: var(--font-weight-bold);
      color: var(--color-text-normal);
    }

    .placeholder {
      color: var(--color-text-low);
    }

    .caret {
      display: flex;
      justify-content: center;
      align-items: center;
      transition: all 0.3s;

      &.open {
        transform: rotate(180deg);
      }
    }
  }

  .dropdown {
    position: absolute;
    background-color: var(--color-surface-subtle);
    width: 100%;
    max-height: 80vh;
    overflow-y: auto;
    box-shadow: var(--shadow-md);
    border-radius: 0 0 5px 5px;
    z-index: 1;
    color: var(--color-text-normal);

    .item {
      box-sizing: border-box;
      padding: 10px 20px;
      color: var(--color-text-normal);

      &:hover {
        color: var(--color-text-on-fixed-bg);
        background-color: var(--color-primary);
      }
    }
  }
}

.section-title {
  box-sizing: border-box;
  padding: 10px 20px;
  border-block: 1px solid var(--color-primary);
  background-color: var(--color-surface-subtle);
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1)); // TODO: drop-shadow用トークン未定義のため暫定値
  color: var(--color-text-normal);

  aside {
    font-size: 0.8em;
    color: var(--color-text-low);
  }
}

.select-box-container {
  --disabled-emphasis: 0.18;

  display: flex;
  align-items: center;
  height: 80px;
  transition: all 0.3s;
  border-bottom: 1px solid var(--color-border);
  background-color: var(--color-surface-subtle);
  color: var(--color-text-normal);

  &.selected-item {
    background-color: var(--color-primary);
    color: var(--color-text-on-fixed-bg);
  }

  &.disabled {
    background-color: var(--color-surface-muted);
    opacity: var(--disabled-emphasis);
  }

  &:not(.disabled):hover {
    background-color: var(--color-primary);
    color: var(--color-text-on-fixed-bg);

    .nola-novel-title {
      color: var(--color-text-on-fixed-bg);
    }
  }

  .image {
    height: 50px;
    width: 50px;
    object-fit: contain;
    margin-right: 10px;
    margin-left: 10px;
  }

  .name {
    flex: 1;
    font-weight: var(--font-weight-bold);
    display: block;
    overflow: hidden;
    margin-right: 5px;
    color: var(--color-text-normal);

    .novel-title {
      // 最大2行まで表示して、はみ出た部分は省略記号で表示
      text-overflow: ellipsis;
      overflow: hidden;
      line-clamp: 2;
      -webkit-line-clamp: 2;

      display: -webkit-box;
      -webkit-box-orient: vertical;
      color: var(--color-text-normal);
    }

    .nola-novel-title {
      margin-top: 5px;
      font-size: 0.8em;

      display: block;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;

      color: var(--color-text-low);

      &.selected-item {
        color: var(--color-text-on-fixed-bg);
      }
    }
  }

  .label {
    color: var(--color-text-on-fixed-bg);
    font-weight: var(--font-weight-bold);
    padding: 3px 8px;
    text-wrap: nowrap;
    margin-right: 10px;
    background-color: var(--color-inactive);

    &.limited-public {
      background-color: var(--color-primary);
    }

    &.public {
      background-color: var(--color-primary);
    }
  }
}
</style>
